export default class Api {
  private static inFlight = 0;

  private static idleWaiters: (() => void)[] = [];

  public static whenIdle(): Promise<void> {
    if (this.inFlight === 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.idleWaiters.push(resolve);
    });
  }

  private static track(response: Promise<Response>): Promise<Response> {
    this.inFlight += 1;

    return response.finally(() => {
      this.inFlight -= 1;
      if (this.inFlight === 0) {
        this.idleWaiters.splice(0).forEach((resolve) => resolve());
      }
    });
  }

  public static get(url: string, data: object|null = null) {
    return this.request('GET', url, data);
  }

  public static put(url: string, data: object|null = null) {
    return this.request('PUT', url, data);
  }

  public static patch(url: string, data: object|null = null) {
    return this.request('PATCH', url, data);
  }

  public static delete(url: string, data: object|null = null) {
    return this.request('DELETE', url, data);
  }

  public static post(url: string, data: object|null = null) {
    return this.request('POST', url, data);
  }

  public static request(
    method: string,
    url: string,
    body: FormData|object|null,
    params: object|null = null,
    signal: AbortSignal|null = null,
  ) {
    const headers = new Headers();
    let processedUrl = url;
    if (method === 'GET' && params) {
      processedUrl = this.formatUrl(url, params);
    }

    if (!(body instanceof FormData)) {
      headers.append('Content-Type', 'application/json');
    }

    const csrfToken = document.querySelector('meta[name="x-csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
      headers.append('X-CSRF-TOKEN', csrfToken);
    }

    let data = null;
    if (body) {
      data = JSON.stringify(body);
      if (body instanceof FormData) {
        data = body;
      }
    }
    return this.track(fetch(processedUrl, {
      method,
      headers,
      body: data,
      signal,
    })).then((res) => {
      if (res.status === 511 || res.status === 401) {
        window.location.href = '/login';
      }
      if (res.status === 503) {
        window.location.href = '/maintenance';
      }
      return res;
    });
  }

  public static requestBinary(
    method: string,
    url: string,
    body: FormData|object|null = null,
    params: object|null = null,
  ) {
    const headers = new Headers();
    let processedUrl = url;
    if (method === 'GET' && params) {
      processedUrl = this.formatUrl(url, params);
    }

    if (!(body instanceof FormData)) {
      headers.append('Content-Type', 'application/json');
    }

    const csrfToken = document.querySelector('meta[name="x-csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
      headers.append('X-CSRF-TOKEN', csrfToken);
    }

    let data = null;
    if (body) {
      data = JSON.stringify(body);
      if (body instanceof FormData) {
        data = body;
      }
    }
    return this.track(fetch(processedUrl, {
      method,
      headers,
      body: data,
    })).then((res) => {
      if (res.status === 511 || res.status === 401) {
        window.location.href = '/login';
      }
      if (res.status === 503) {
        window.location.href = '/maintenance';
      }
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      return res.blob();
    });
  }

  private static formatUrl(url: string, params: DataObjectInterface): string {
    const paramsString = Object.keys(params)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');

    return paramsString ? `${url}?${paramsString}` : url;
  }
}
